import { IBookletModule } from "interfaces/IBookletModule";
import { IContentPerType, IContentPerTypeItem } from "interfaces/IContentPerType";
import { IRelatedVideo } from "interfaces/IRelatedContent";
import { ITeacher } from "interfaces/ITeacher";
import { IVideo } from "interfaces/IVideo";
import { createActions, createReducer } from "reduxsauce";
import { LikeStatusType } from "utils/types/LikeStatus";
import { ternary } from "utils/ternary";

export interface IWatchScreenVideo extends IVideo {
    learningObject: {
        id: number;
        viewed?: boolean;
        isLiked?: LikeStatusType;
        openForum?: number;
    };
    playlist?: IRelatedVideo[];
    blockContent?: boolean;
}

export interface IWatchBookletModule extends IBookletModule {
    contentPerType: IContentPerType[];
}

export interface IWatchLessonPlanModule {
    id: number;
    slug: string;
    name: string;
}

export enum IWatchVirtualAssistantMessageRole {
    "Usuario" = 1,
    "Assistente"
}

export interface IWatchVirtualAssistantMessage {
    id?: string;
    content: string;
    role: IWatchVirtualAssistantMessageRole;
}

export interface IWatchVirtualAssistantMessages {
    isSending: boolean;
    items: IWatchVirtualAssistantMessage[];
}

export interface IWatchVirtualAssistantData {
    messages: IWatchVirtualAssistantMessages;
    suggestions: string[];
}

export interface IWatchVirtualAssistantState {
    data: IWatchVirtualAssistantData;
    isLoading: boolean;
}

export interface IWatchSubjectSlug {
    name: string;
    color: string;
    id: number;
}
export interface IWatchState {
    isLoading: boolean;
    eventStartDate?: string;
    eventEndDate?: string;
    id: number;
    date: string;
    title: string;
    teachers: ITeacher[];
    materials: any[];
    booklets: any[];
    exerciseLists: any[];
    video: IWatchScreenVideo;
    bookletModule: IWatchBookletModule;
    relatedVideos?: IContentPerTypeItem[];
    category: number;
    module?: IWatchLessonPlanModule;
    subjectObj?: IWatchSubjectSlug;
    hasForum: boolean;
    eventId: number;
    virtualAssistant?: IWatchVirtualAssistantState;
}

export const { Creators, Types } = createActions({
    setWatch: ["payload"],
    setCurrentVideo: ["payload"],
    setCurrentVideoData: ["payload"],

    setWatchLikeRequest: ["payload"],
    setWatchLikeSuccess: ["payload"],
    setWatchLikeFailure: ["payload"],

    setViewedVideoRequest: ["payload"],
    setViewedVideoSuccess: ["payload"],
    setViewedVideoFailure: ["payload"],

    getWatchBookletModuleRequest: ["payload"],
    getWatchBookletModuleSuccess: ["payload"],
    getWatchBookletModuleFailure: ["payload"],

    getWatchVideoRequest: ["payload"],
    getWatchVideoSuccess: ["payload"],
    getWatchVideoFailure: ["payload"],

    getWatchLessonPlanRequest: ["payload"],
    getWatchLessonPlanSuccess: ["payload"],
    getWatchLessonPlanFailure: ["payload"],

    getVirtualAssistantAnswersRequest: ["payload"],
    getVirtualAssistantAnswersSuccess: ["payload"],
    getVirtualAssistantAnswersFailure: ["payload"],

    sendVirtualAssistantQuestionRequest: ["payload"],
    sendVirtualAssistantQuestionSuccess: ["payload"],
    sendVirtualAssistantQuestionFailure: ["payload"],

    clearWatchVideo: []
});

export const INITIAL_STATE: IWatchState = {
    isLoading: false,
    id: 0,
    date: "",
    title: "",
    teachers: [],
    materials: [],
    booklets: [],
    exerciseLists: [],
    hasForum: false,
    eventId: 0,
    video: {
        created: "",
        videoHash: null,
        blockContent: false,
        code: "",
        description: "",
        hashVideo: "",
        id: 0,
        openForum: 0,
        provider: 0,
        title: "",
        url: "",
        videoKey: "",
        subject: {
            color: "",
            id: 0,
            image: "",
            name: "",
            order: 0,
            slug: "",
            progress: {
                completedItems: 0,
                percent: 0,
                totalItems: 0
            }
        },
        totalViews: 0,
        learningObject: {
            id: 0
        }
    },
    bookletModule: {
        code: "",
        created: "",
        progress: {
            completedItems: -1,
            totalItems: -1,
            percent: -1
        },
        stats: [],
        subject: {
            color: "",
            id: -1,
            image: "",
            name: "",
            order: -1,
            progress: {
                completedItems: -1,
                percent: -1,
                totalItems: -1
            },
            slug: ""
        },
        subjectFront: {},
        content: [],
        id: 0,
        name: "",
        order: 0,
        contentPerType: []
    },
    category: -1,
    virtualAssistant: {
        isLoading: false,
        data: {
            messages: {
                isSending: false,
                items: []
            },
            suggestions: []
        }
    }
};

const setWatch = (state = INITIAL_STATE, action: any) => ({ ...state, ...action.payload });

const setCurrentVideo = (state = INITIAL_STATE, action: any) => ({
    ...state,
    video: action.payload
});

const setCurrentVideoData = (state = INITIAL_STATE, action: any) => ({
    ...state
});

const getVideoSuccess = (state = INITIAL_STATE, action: any) => ({
    ...state,
    video: action.payload,
    isLoading: false
});

const getBookletModuleSuccess = (state = INITIAL_STATE, action: any) => ({
    ...state,
    bookletModule: action.payload,
    isLoading: false
});

const getLessonPlanSuccess = (state = INITIAL_STATE, action: any) => ({
    ...state,
    isLoading: false
});

const setLikeSuccess = (state = INITIAL_STATE, action: any) => {
    const { status } = action.payload;

    const updateVideo = {
        ...state.video,
        learningObject: {
            ...state.video.learningObject,
            isLiked: status
        }
    };

    return {
        ...state,
        video: updateVideo
    };
};

// VIRTUAL ASSISTANT ACTIONS

const getVirtualAssistantAnswersRequest = (state = INITIAL_STATE, action: any) => ({
    ...state,
    virtualAssistant: {
        ...state.virtualAssistant,
        isLoading: true
    }
});
const getVirtualAssistantAnswersSuccess = (state = INITIAL_STATE, action: any) => ({
    ...state,
    virtualAssistant: {
        isLoading: false,
        data: {
            ...state.virtualAssistant?.data,
            messages: {
                ...state.virtualAssistant?.data.messages,
                items: action.payload
            }
        }
    }
});
const getVirtualAssistantAnswersFailure = (state = INITIAL_STATE, action: any) => ({
    ...state,
    virtualAssistant: {
        ...state.virtualAssistant,
        isLoading: false
    }
});

const sendVirtualAssistantQuestionRequest = (state = INITIAL_STATE, action: any) => ({
    ...state,
    virtualAssistant: {
        ...state.virtualAssistant,
        data: {
            ...state.virtualAssistant?.data,
            messages: {
                ...state.virtualAssistant?.data.messages,
                isSending: true
            }
        }
    }
});
const sendVirtualAssistantQuestionSuccess = (state = INITIAL_STATE, action: any) => {
    const { items, isSending, suggestions } = action.payload;

    return {
        ...state,
        virtualAssistant: {
            ...state.virtualAssistant,
            data: {
                ...state.virtualAssistant?.data,
                ...(!!suggestions && { suggestions }),
                messages: {
                    items,
                    isSending
                }
            }
        }
    };
};
const sendVirtualAssistantQuestionFailure = (state = INITIAL_STATE, action: any) => {
    const errorMessage = action.payload;

    const formattedMessages = state.virtualAssistant?.data?.messages.items.map(
        (message: IWatchVirtualAssistantMessage, index: number): IWatchVirtualAssistantMessage => {
            const { id, role, content } = message;

            const isLastMessage = index === (state.virtualAssistant?.data?.messages?.items?.length ?? 0) - 1;

            const formattedContent = ternary([
                [!!isLastMessage && role === IWatchVirtualAssistantMessageRole["Assistente"], errorMessage],
                [!!isLastMessage && role === IWatchVirtualAssistantMessageRole["Usuario"], content],
                [!isLastMessage && role === IWatchVirtualAssistantMessageRole["Assistente"], content],
                [!isLastMessage && role === IWatchVirtualAssistantMessageRole["Usuario"], content]
            ]);

            return {
                id,
                role,
                content: formattedContent
            };
        }
    );

    return {
        ...state,
        virtualAssistant: {
            ...state.virtualAssistant,
            data: {
                ...state.virtualAssistant?.data,
                messages: {
                    ...state.virtualAssistant?.data.messages,
                    items: formattedMessages,
                    isSending: false
                }
            }
        }
    };
};

// GENERIC ACTIONS

const watchWithoutLoading = (state = INITIAL_STATE, action: any) => ({
    ...state
});

const watchRequest = (state = INITIAL_STATE, action: any) => ({
    ...state,
    isLoading: true
});

const watchFailure = (state = INITIAL_STATE, action: any) => ({
    ...state,
    isLoading: false
});

const clearVideo = (state = INITIAL_STATE) => INITIAL_STATE;

export default createReducer(INITIAL_STATE, {
    [Types.SET_WATCH]: setWatch,
    [Types.SET_CURRENT_VIDEO]: setCurrentVideo,
    [Types.SET_CURRENT_VIDEO_DATA]: setCurrentVideoData,

    // BOOKLET MODULE
    [Types.GET_WATCH_BOOKLET_MODULE_REQUEST]: watchRequest,
    [Types.GET_WATCH_BOOKLET_MODULE_SUCCESS]: getBookletModuleSuccess,
    [Types.GET_WATCH_BOOKLET_MODULE_FAILURE]: watchFailure,

    // LESSON PLAN
    [Types.GET_WATCH_LESSON_PLAN_REQUEST]: watchRequest,
    [Types.GET_WATCH_LESSON_PLAN_SUCCESS]: getLessonPlanSuccess,
    [Types.GET_WATCH_LESSON_PLAN_FAILURE]: watchFailure,

    // VIDEO
    [Types.GET_WATCH_VIDEO_REQUEST]: watchRequest,
    [Types.GET_WATCH_VIDEO_SUCCESS]: getVideoSuccess,
    [Types.GET_WATCH_VIDEO_FAILURE]: watchFailure,

    // LIKE VIDEO
    [Types.SET_WATCH_LIKE_REQUEST]: watchWithoutLoading,
    [Types.SET_WATCH_LIKE_SUCCESS]: setLikeSuccess,
    [Types.SET_WATCH_LIKE_FAILURE]: watchFailure,

    // VIRUTAL ASSISTANT GET ANSWERS
    [Types.GET_VIRTUAL_ASSISTANT_ANSWERS_REQUEST]: getVirtualAssistantAnswersRequest,
    [Types.GET_VIRTUAL_ASSISTANT_ANSWERS_SUCCESS]: getVirtualAssistantAnswersSuccess,
    [Types.GET_VIRTUAL_ASSISTANT_ANSWERS_FAILURE]: getVirtualAssistantAnswersFailure,

    // VIRTUAL ASSISTANT SEND QUESTION
    [Types.SEND_VIRTUAL_ASSISTANT_QUESTION_REQUEST]: sendVirtualAssistantQuestionRequest,
    [Types.SEND_VIRTUAL_ASSISTANT_QUESTION_SUCCESS]: sendVirtualAssistantQuestionSuccess,
    [Types.SEND_VIRTUAL_ASSISTANT_QUESTION_FAILURE]: sendVirtualAssistantQuestionFailure,

    // CLEAR
    [Types.CLEAR_WATCH_VIDEO]: clearVideo
});
